import { LoaderCircle, RotateCcw, X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { normalizeCustomerCountry } from "../../../utils/productTradability";
import { formatPercentage } from "../../../utils/numberUtils";
import {
  getProductRestrictionKey,
  useProductRestrictionDetails,
  type ProductRestrictionRequestState,
} from "../../../hooks/useProductRestrictionDetails";
import type {
  ProductRestrictionDetails,
  ProductRestrictionProductType,
} from "../../../services/productRestrictionsService";

export interface TradabilityModalProduct {
  productType: ProductRestrictionProductType;
  productId: number;
  title: string;
}

interface ProductTradabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  confirmLabel?: string;
  products: TradabilityModalProduct[];
  customerCountry: string | null;
  appearance?: "default" | "alpha";
}

export const TRADABILITY_MODAL_TITLE = "Trading restrictions";

function restrictionReasonText(flag: string): string {
  switch (flag) {
    case "etf-non-us":
      return "It holds a non-US ETF, which your account cannot trade.";
    case "etf-us":
      return "It holds a US ETF, which your account cannot trade.";
    case "ptp":
      return "It holds a publicly traded partnership (PTP), which your account cannot trade.";
    case "has-shorts":
      return "It holds short positions, which retirement accounts cannot trade.";
    default:
      return "It holds a contract that is restricted for your account.";
  }
}

function clampPercentage(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function RestrictionDetails({
  details,
  onConfirm,
  appearance,
}: {
  details: ProductRestrictionDetails;
  onConfirm?: () => void;
  appearance: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const restrictedPct = details.tradability.restrictedGrossPct;
  const restrictedHoldingCount = details.tradability.restrictedHoldingCount;
  const normalizedRestrictedPct =
    restrictedPct === null ? null : clampPercentage(restrictedPct);
  const tradablePct =
    normalizedRestrictedPct === null ? null : 100 - normalizedRestrictedPct;
  const formattedRestrictedPct =
    normalizedRestrictedPct === null
      ? null
      : formatPercentage(normalizedRestrictedPct, 1);
  const formattedTradablePct =
    tradablePct === null ? null : formatPercentage(tradablePct, 1);
  const hasRestrictions =
    details.restrictionFlags.length > 0 ||
    (normalizedRestrictedPct !== null && normalizedRestrictedPct > 0) ||
    (restrictedHoldingCount !== null && restrictedHoldingCount > 0);

  return (
    <div className="space-y-4">
      <p className={`text-sm leading-relaxed ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-300"}`}>
        {hasRestrictions
          ? "Some holdings in this product cannot be traded by your account. Those holdings are skipped and the rest is bought normally."
          : "No trading restrictions currently apply to this product for your account."}
      </p>

      {normalizedRestrictedPct !== null &&
        tradablePct !== null &&
        formattedRestrictedPct !== null &&
        formattedTradablePct !== null && (
        <div className={`border p-4 ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-paper" : "rounded-lg border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-sunken"}`}>
          <div className="flex items-baseline gap-2">
            <span className={`font-mono text-2xl font-bold ${isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-500"}`}>
              {formattedRestrictedPct}
            </span>
            <span className={`text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-300"}`}>
              of this product&apos;s gross exposure cannot be traded by your account
            </span>
          </div>
          <div
            className={`mt-3 flex h-2 overflow-hidden rounded-full ${isAlpha ? "bg-v7-mist" : "bg-gray-200 dark:bg-gray-800"}`}
            role="img"
            aria-label={`${formattedTradablePct} tradable and ${formattedRestrictedPct} restricted`}
          >
            <div
              className={isAlpha ? "bg-v7-success" : "bg-green-600 dark:bg-green-500"}
              style={{ width: `${tradablePct}%` }}
            />
            <div
              className={isAlpha ? "bg-v7-rust" : "bg-red-600 dark:bg-red-500"}
              style={{ width: `${normalizedRestrictedPct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider">
            <span className={isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"}>
              Tradable {formattedTradablePct}
            </span>
            <span className={isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-500"}>
              Restricted {formattedRestrictedPct}
            </span>
          </div>
        </div>
      )}

      {restrictedHoldingCount !== null && (
        <p className={`text-sm leading-relaxed ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-300"}`}>
          {restrictedHoldingCount === 0
            ? "No holdings are restricted for your account."
            : `${restrictedHoldingCount} ${restrictedHoldingCount === 1 ? "holding" : "holdings"} cannot be traded by your account.`}
        </p>
      )}

      {details.restrictionFlags.length > 0 ? (
        <ul className="space-y-2">
          {details.restrictionFlags.map((flag) => (
            <li key={flag} className="flex items-start gap-2">
              <span className={`mt-0.5 shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] ${isAlpha ? "border-v7-ink/15 bg-v7-canvas text-v7-ink/60" : "border-gray-200 bg-gray-50 text-gray-600 dark:border-rp-separator dark:bg-rp-surface-raised dark:text-gray-400"}`}>
                {flag}
              </span>
              <span className={`text-sm leading-relaxed ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-300"}`}>
                {restrictionReasonText(flag)}
              </span>
            </li>
          ))}
        </ul>
      ) : normalizedRestrictedPct === null && restrictedHoldingCount === null ? (
        <p className={`text-sm leading-relaxed ${isAlpha ? "text-v7-ink/60" : "text-gray-600 dark:text-gray-400"}`}>
          Complete restriction details are unavailable for this product.
        </p>
      ) : null}

      <p className={`text-xs leading-relaxed ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
        {normalizedRestrictedPct === null
          ? "The affected share of the product is unavailable."
          : "The share is measured gross across every holding, including holdings inside baskets, so long and short positions cannot cancel out."}
        {onConfirm && " Adding does not make the restricted holdings tradable."}
      </p>
    </div>
  );
}

function RestrictionRequestContent({
  state,
  retry,
  onConfirm,
  appearance,
}: {
  state: ProductRestrictionRequestState;
  retry: () => void;
  onConfirm?: () => void;
  appearance: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  if (state.status === "loading") {
    return (
      <div role="status" className={`flex items-center gap-2 py-6 text-sm ${isAlpha ? "text-v7-ink/60" : "text-gray-600 dark:text-gray-300"}`}>
        <LoaderCircle className={`h-4 w-4 animate-spin ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`} />
        Loading restriction details...
      </div>
    );
  }

  if (state.status === "not-found") {
    return (
      <div className={isAlpha
        ? "rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 p-4 text-sm text-v7-bronze-dark"
        : "rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"}>
        Restriction details are unavailable because this product is private or no longer exists.
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div role="alert" className={`border p-4 ${isAlpha ? "rounded-v7-control border-v7-rust/30 bg-v7-rust/5" : "rounded-lg border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"}`}>
        <p className={`text-sm ${isAlpha ? "text-v7-rust" : "text-red-700 dark:text-red-400"}`}>
          Restriction details could not be loaded.
        </p>
        <button
          type="button"
          onClick={retry}
          className={`mt-3 inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-semibold transition-colors ${
            isAlpha
              ? "rounded-v7-control border-v7-rust/30 bg-v7-white text-v7-rust hover:border-v7-rust"
              : "rounded-lg border-red-200 bg-white text-red-700 hover:border-red-300 dark:border-red-800 dark:bg-rp-surface dark:text-red-400"
          }`}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return <RestrictionDetails details={state.details} onConfirm={onConfirm} appearance={appearance} />;
}

const ProductTradabilityModal: React.FC<ProductTradabilityModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  confirmLabel = "Add anyway",
  products,
  customerCountry,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const normalizedCountry = normalizeCustomerCountry(customerCountry);
  const { detailsByProduct, retry } = useProductRestrictionDetails(products, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  const isSingle = products.length === 1;

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm ${isAlpha ? "bg-v7-ink/70" : "bg-black/70"}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="tradability-modal-title"
        data-testid="tradability-modal-panel"
        data-appearance={appearance}
        className={`w-full max-w-lg overflow-hidden border shadow-2xl ${
          isAlpha
            ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans text-v7-ink"
            : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"
        }`}
      >
        <div className={`flex items-start justify-between gap-4 border-b px-5 py-4 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
          <div className="min-w-0">
            <h2
              id="tradability-modal-title"
              className={isAlpha ? "text-xl font-medium tracking-[-0.02em] text-v7-ink" : "text-lg font-bold text-gray-900 dark:text-white"}
            >
              {TRADABILITY_MODAL_TITLE}
            </h2>
            <p className={`mt-1 truncate text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
              {isSingle && (
                <>
                  <span className={isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-200"}>
                    {products[0].title}
                  </span>
                  {" · "}
                </>
              )}
              Account country:{" "}
              <span className={`font-mono font-semibold uppercase ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-200"}`}>
                {normalizedCountry?.toUpperCase() ?? "Not configured"}
              </span>
              {!normalizedCountry && " (treated as US)"}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close tradability details"
            className={isAlpha
              ? "flex h-9 w-9 shrink-0 items-center justify-center rounded-v7-control border border-v7-ink/20 bg-v7-white text-v7-ink/60 transition-colors hover:border-v7-bronze-dark hover:text-v7-bronze-dark"
              : "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-rp-amber hover:text-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-gray-400"}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4">
          {products.map((product) => {
            const key = getProductRestrictionKey(product);
            const state = detailsByProduct[key] ?? { status: "loading" };
            return (
              <div
                key={key}
                className={`border p-4 ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-canvas" : "rounded-lg border-gray-200 dark:border-rp-separator"}`}
              >
                {!isSingle && (
                  <h3 className={`mb-3 text-sm font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                    {product.title}
                  </h3>
                )}
                <RestrictionRequestContent
                  state={state}
                  retry={retry}
                  onConfirm={onConfirm}
                  appearance={appearance}
                />
              </div>
            );
          })}
        </div>

        <div className={`flex justify-end gap-3 border-t px-5 py-4 ${isAlpha ? "border-v7-ink/15 bg-v7-paper/50" : "border-gray-200 dark:border-rp-separator"}`}>
          <button
            type="button"
            onClick={onClose}
            className={isAlpha
              ? "min-h-10 rounded-v7-control border border-v7-ink/30 bg-v7-white px-5 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink"
              : "rounded-lg border border-gray-200 bg-white px-5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
          >
            {onConfirm ? "Cancel" : "Close"}
          </button>
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              className={isAlpha
                ? "min-h-10 rounded-v7-control bg-v7-ink px-5 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
                : "rounded-lg bg-rp-amber px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
};

export default ProductTradabilityModal;
